import { useState } from "react";
import { useTranslation } from "@i18n";
import { BsExclamationCircle } from "react-icons/bs";

import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Text from "@CubeComponents/html/Text/Text";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import {
  previewPattern,
  validateRevisionAgainstRules,
  type RevisionRuleViolation,
  type RevisionSequenceRules,
  type RevisionSequenceType,
} from "@Helpers/revisionPattern";

type Props = {
  versionNumber?: number;
  startPattern: string | null;
  sequenceType?: RevisionSequenceType | null;
  previousLabel: string | null;
  existingLabels: string[];
  rules: RevisionSequenceRules;
  isSubmitting: boolean;
  onConfirm: (label: string) => Promise<string | null>;
  onClose: () => void;
};

function MarkRevisionModal({
  versionNumber,
  startPattern,
  sequenceType,
  previousLabel,
  existingLabels,
  rules,
  isSubmitting,
  onConfirm,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  const violationMessages: Record<RevisionRuleViolation, string> = {
    NOT_IN_SERIES: t("Revision is not part of the assigned revision series"),
    MUST_BE_INITIAL: t("First revision must start at the first value of the series"),
    SEQUENTIAL_VIOLATION: t("Revision must be the same as or next after the previous revision"),
    MUST_ADVANCE: t("Revision must be later in the series than the previous revision"),
    BACKWARDS_NOT_ALLOWED: t("Revision cannot be earlier in the series than the previous revision"),
    REPEATED_REVISION: t("This revision is already assigned to another version of this file"),
    REVISION_REQUIRED: t("Revision is mandatory for this folder's Revision Series"),
  };

  const handleConfirm = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setError(t("Please enter a revision"));
      return;
    }
    if (startPattern) {
      const violation = validateRevisionAgainstRules({
        label: trimmed,
        startPattern,
        previousLabel,
        existingLabels,
        rules,
        sequenceType,
      });
      if (violation) {
        setError(violationMessages[violation]);
        return;
      }
    }
    setError(null);
    const serverError = await onConfirm(trimmed);
    if (serverError) setError(serverError);
  };

  const previewValues = startPattern ? previewPattern(startPattern, 4, sequenceType) : [];

  return (
    <ModalStateDialogComponent
      isVisible
      width="31.25rem"
      headerTitle={<p className="text-2xl font-bold">{t("Mark as Revision")}</p>}
      onCancel={onClose}
      modelContent={
        <div className="flex flex-col gap-4 py-2">
          <Text className="text-sm text-gray-600">
            {t("Assigning a revision to Version {{versionNumber}}", { versionNumber: versionNumber ?? "" })}
          </Text>

          <div className="flex flex-col gap-1">
            <Text className="text-base font-medium">
              {t("Revision")}
              <Text className="text-red-500">*</Text>
            </Text>
            <TextFieldComponent
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLabel(e.target.value);
                if (error) setError(null);
              }}
              placeholder={t("Enter")}
            />
            {error && (
              <div className="flex items-center gap-2 mt-1">
                <BsExclamationCircle className="w-4 h-4 shrink-0 text-[#E05650]" />
                <span className="text-[#E05650] text-sm">{error}</span>
              </div>
            )}
          </div>

          {previewValues.length > 0 && (
            <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
              <Text className="text-base font-semibold">{t("Series Pattern")}</Text>
              <div className="flex flex-wrap items-center gap-3 text-sm font-semibold">
                {previewValues.map((value, index) => (
                  <span key={index} className="flex items-center gap-3">
                    <span>{value}</span>
                    <span aria-hidden>→</span>
                  </span>
                ))}
                <span>…</span>
              </div>
            </div>
          )}
        </div>
      }
      footerContent={
        <div className="flex gap-4">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} loading={isSubmitting} onClick={handleConfirm}>
            {t("Confirm")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

export default MarkRevisionModal;
